import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  KeyValueDiffer,
  KeyValueDiffers,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { hasParentWithId } from '@finxone-platform/shared/frontend';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  ActionConfig,
  Columns,
  ListingAction,
  SkeletonCell,
  SkeletonLayoutConfig,
  ZoneActionOptions,
  baseCssConfigStyle,
  buildSkeletonTableCells,
  cssToNgStyle,
  deriveSkeletonTableRowCount,
  getSkeletonTableCell,
} from '@finxone-platform/shared/sys-config-types';
import * as Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';

/** Used only for a column beyond the number of columns a skeleton is built for. */
const SKELETON_FALLBACK_CELL: SkeletonCell = {
  width: '100px',
  height: '16px',
};

@Component({
  standalone: false,
  selector: 'finx-table',
  templateUrl: './finx-table.component.html',
  styleUrls: ['./finx-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FinxTableComponent implements OnInit, OnChanges, DoCheck {
  private differs = inject(KeyValueDiffers);
  private iconCacheService = inject(IconCacheService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('tableTopAnchor') tableTopAnchor: ElementRef<HTMLElement>;
  public readonly tableTopAnchorId = `finx-table-top-anchor-${crypto.randomUUID().slice(0, 8)}`;

  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() options = new EventEmitter<{ id: string; res: any }>();
  @Output() actionEvent = new EventEmitter<{ actionConfig: ActionConfig; data: any }>();
  @Output() navigateToUrl = new EventEmitter<{ link: string; id: string; actionOption: ZoneActionOptions }>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() pageChange = new EventEmitter<any>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() closeMenuPopPup = new EventEmitter<any>();
  @Output() searchTextEmit = new EventEmitter<string>();
  @Output() checkboxSelection = new EventEmitter<string[]>();
  @Output() tableHeaderCheckboxSelection = new EventEmitter<any[]>();
  @Output() rowData = new EventEmitter<any>();
  @Output() dropDownValue = new EventEmitter<string>();
  @Output() confirmButtonAction = new EventEmitter<any>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() entityData: any[] = [];
  @Input() pageSize: number;
  @Input() actionOptions: ZoneActionOptions[];
  @Input() pageNumber: number;
  @Input() isShowMoreOption: boolean;
  @Input() entityName: string;
  @Input() isShowOption: boolean;
  @Input() totalListCount: number;
  @Input() isSelectedId = '';
  @Input() isClickMenuIcon: boolean;
  @Input() menuPopupCss: object;
  @Input() emptyTableMsg = ' No Data Found';
  @Input() threeDotStyles: object;
  @Input() columns: Columns[] = [];
  @Input() pagingEnabled = true;
  @Input() contentStyle: baseCssConfigStyle;
  @Input() viewPaginationOptions: string[] = [];
  @Input() enableSearch = true;
  @Input() searchPlaceHolder = 'search...';
  @Input() searchIcon = 'search';
  @Input() currentSearchInputStyle: baseCssConfigStyle;
  @Input() currentActiveStyle: baseCssConfigStyle;
  @Input() currentInactiveStyle: baseCssConfigStyle;
  @Input() searchIconStyle: baseCssConfigStyle;
  @Input() selectedTableSize = '';
  @Input() tableHeaderStyles: baseCssConfigStyle;
  @Input() gridLinesEnabled = false;
  @Input() tableGridLines: baseCssConfigStyle;
  @Input() tableRowStyle: baseCssConfigStyle;
  @Input() public multipleSelectionAllowed = false;
  @Input() menuItemsStatus: {
    [key: string]: { [key: string]: boolean };
  };
  @Input() actionButtonContent = '';
  @Input() confirmButtonStyle = {};
  @Input() dropdownOptions: { label: string; value: string }[];
  @Input() checkboxStyles: {
    checkboxCurrentStyle: baseCssConfigStyle;
    checkboxInactiveStyle: baseCssConfigStyle;
    checkboxActiveStyle: baseCssConfigStyle;
  };
  @Input() enableClickableRow = false;
  @Input() tableHeaderCustomCss = '';
  @Input() tableRowCustomCss = '';
  @Input() isApiInProgress = false;
  @Input() fetchingContent = '';
  @Input() skeletonConfig?: SkeletonLayoutConfig;

  @Input() tableRowBgColour = {
    selectRowBackgroundStyle: 'single-colour',
    oddRowBgColor: 'var(--color-surface)',
    evenRowBgColor: 'var(--color-surface)',
  };
  @Input() searchInputStyle = {
    width: '25%',
  };
  @Input() tableToolbarHeaderStyle = { 'background-color': 'var(--color-surface)', 'justify-content': 'end' };
  @Input() dropdownAction: { isAllowDropdownAction: boolean; dropdownLabel: string; dropdownWidth: number };
  @Input() showConfirmButton = false;
  @Input() selectedOption: { label: string; value: string };
  @Input() uniqueId = 'id'; // Default is set to 'id', but can be changed to any other property name via input binding.
  @Input() totalListDisabled = true;
  @Input() listActions: ListingAction;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public tableHeaderColumns: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public templateFunction: HandlebarsTemplateDelegate<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public templateColumnFunction: HandlebarsTemplateDelegate<any>;
  public currentPageReport: string;
  public searchText: string;
  public viewPaginationOptionValue: number[] = [];
  public selectedItemIds: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public selectedItemsFromHeaderCheckbox: any[] = [];
  public selectedItem: string[] = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private differ: KeyValueDiffer<any, any>;
  public tableHeaderCss: { [key: string]: string };
  public tableRowCss: { [key: string]: string };
  public totalPageItemsCount = 0;
  public first = 0;
  public actionButtonStyle = {};
  private skeletonCellsCache?: { key: string; cells: SkeletonCell[] };

  constructor() {
    this.differ = this.differs.find({}).create();
  }

  ngOnInit() {
    this.buildTableStyles();
    this.initPaginationState();
    this.onLoadHandlerTemplate();
    this.subscribeToIconLoad();
    this.buildActionButtonStyle();
  }

  private buildTableStyles() {
    this.tableHeaderCss = cssToNgStyle(
      this.tableHeaderCustomCss ??
        `font-size: 16px; font-weight: 700; color:var(--color-on-surface-muted); padding:15px 10px 15px 24px`,
    );
    this.tableRowCss = cssToNgStyle(
      this.tableRowCustomCss ?? `padding: 1.6rem 2.4rem; font-size: 16px; font-weight: 400;`,
    );
  }

  private initPaginationState() {
    this.viewPaginationOptionValue = this.viewPaginationOptions?.map((option) => parseInt(option)) ?? [];
    this.currentPageReport = `Showing 1 to ${this.pageSize} of ${this.totalListCount} entries`;
  }

  private subscribeToIconLoad() {
    this.iconCacheService.iconLoaded$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  private buildActionButtonStyle() {
    this.actionButtonStyle = {
      gap: `${this.listActions?.rowGap ?? 0}px`,
    };
  }

  public renderHandlebarsTemplate(templateString: string, rowData: any): string {
    const template = Handlebars.compile(templateString);
    return template(rowData);
  }

  public onClickAction(listAction: any, rowData: any) {
    this.actionEvent.emit({
      actionConfig: listAction,
      data: rowData,
    });
  }

  public onHeaderCheckboxChange(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedItemsFromHeaderCheckbox = checked ? [...this.entityData] : [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onRowCheckboxChange(event: Event, rowData: any) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedItemsFromHeaderCheckbox = [...this.selectedItemsFromHeaderCheckbox, rowData];
    } else {
      this.selectedItemsFromHeaderCheckbox = this.selectedItemsFromHeaderCheckbox.filter(
        (item) => item[this.uniqueId] !== rowData[this.uniqueId],
      );
    }
    this.handleCheckboxSelection({ data: rowData }, checked);
  }

  public onPrevPage() {
    const rows = this.pageSize;
    const first = Math.max(0, this.first - rows);
    this.onPageChange(this.buildPageEvent(first, rows));
    this.scrollToTopOnPaginationChange();
  }

  public onNextPage() {
    const rows = this.pageSize;
    const first = this.first + rows;
    this.onPageChange(this.buildPageEvent(first, rows));
    this.scrollToTopOnPaginationChange();
  }

  public onRowsPerPageChange(event: Event) {
    const rows = parseInt((event.target as HTMLSelectElement).value, 10);
    this.onPageChange(this.buildPageEvent(0, rows));
  }

  private pendingScrollToTop = false;

  private scrollToTopOnPaginationChange(): void {
    this.pendingScrollToTop = true;
  }

  private scrollPageToTop(): void {
    this.tableTopAnchor?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  public isLastPage(): boolean {
    if (isNaN(this.totalListCount)) {
      return false;
    }
    return this.first + this.pageSize >= this.totalListCount;
  }

  private buildPageEvent(first: number, rows: number) {
    const pageCount = rows > 0 ? Math.ceil((this.totalListCount || 0) / rows) : 0;
    return { first, rows, pageCount, page: Math.floor(first / rows) };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['totalListCount']) {
      this.updateCurrentPageReport({ first: 0, rows: this.pageSize });
      this.first = 0;
    }
    if (changes['entityData'] && this.pendingScrollToTop) {
      this.pendingScrollToTop = false;
      requestAnimationFrame(() => this.scrollPageToTop());
    }
  }

  ngDoCheck(): void {
    if (this.multipleSelectionAllowed) {
      const changes = this.differ.diff(this.selectedItemsFromHeaderCheckbox);
      if (changes) {
        if (this.selectedItemsFromHeaderCheckbox?.length > 0) {
          this.checkUncheckCheckboxAll(true);
        } else {
          this.checkUncheckCheckboxAll(false);
        }
        this.tableHeaderCheckboxSelection.emit(this.selectedItemsFromHeaderCheckbox);
      }
    }
  }

  public onLoadHandlerTemplate() {
    let tableData = '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let tableHeader: any[] = [];
    const className = this.enableClickableRow ? 'cursor-pointer' : '';
    this.columns.forEach((item) => {
      tableData = tableData.concat(
        `<td id="td-data" class="${className}"><span id="td-data">${item.handleBarKey}</span></td>`,
      );
    });

    if (this.isShowMoreOption) {
      tableHeader = tableHeader.concat(`
        <th class="width-action-row">&nbsp;</th>
      `);
    }

    this.templateFunction = Handlebars.compile(desktopUserTemplate(tableData));
    this.templateColumnFunction = Handlebars.compile(desktopUserColumnTemplate(tableHeader));
    this.tableHeaderColumns = this.templateColumnFunction({});
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public renderUserItem(txData: any): string {
    return this.templateFunction({ ...txData });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public moreOption(id: string, res: any) {
    this.options.emit({ id, res });
  }

  public navigateTo(link: string, id: string, actionOption: ZoneActionOptions) {
    this.navigateToUrl.emit({ link, id, actionOption });
    this.isShowOption = false;
  }
  public closeMenu() {
    this.closeMenuPopPup.emit();
  }

  public onPageChange(event: { first: number; page?: number; pageCount?: number; rows: number }) {
    this.totalPageItemsCount = event.first;
    this.first = event.first;

    // Explicitly calculate page from first/rows as safeguard
    // event.page is 0-indexed (0 = page 1), add 1 for 1-indexed if needed
    const calculatedPage = Math.floor(event.first / event.rows);
    const pageEvent = {
      ...event,
      page: (event.page ?? calculatedPage) + 1, // 1-indexed for convenience
    };

    this.pageChange.emit(pageEvent);
    this.updateCurrentPageReport(event);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public loadCustomers(event: any) {
    this.updateCurrentPageReport(event);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateCurrentPageReport(event: any) {
    this.selectedItemsFromHeaderCheckbox = [];
    const first = event.first + 1;
    const value = parseInt(event.first) + parseInt(event.rows);
    if (isNaN(this.totalListCount)) {
      const last = value;
      this.currentPageReport = `Showing ${first} to ${last}`;
    } else {
      const last = Math.min(value, this.totalListCount);
      this.currentPageReport = `Showing ${first} to ${last} of ${this.totalListCount} entries`;
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public searchValue(event: any) {
    this.searchText = event.target.value;
    this.searchTextEmit.emit(this.searchText);
  }

  public handleCheckboxSelection(event: { data?: any }, isSelected: boolean) {
    const itemId = event.data?.[this.uniqueId];
    if (!itemId) return;
    if (isSelected) {
      this.selectedItemIds.push(itemId);
      this.selectedItem.push(itemId);
      this.checkboxStyles.checkboxCurrentStyle = this.checkboxStyles.checkboxActiveStyle;
    } else {
      const indexToRemove = this.selectedItemIds.findIndex((v) => v === itemId);
      this.selectedItemIds.splice(indexToRemove, 1);
      this.selectedItem.splice(indexToRemove, 1);
      this.checkboxStyles.checkboxCurrentStyle = this.checkboxStyles.checkboxInactiveStyle;
    }
    this.checkboxSelection.emit(this.selectedItemIds);
  }

  // This function is applies the styles for the active and inactive checkboxes.
  private checkUncheckCheckboxAll(isSelected: boolean) {
    if (isSelected) {
      this.selectedItem = this.selectedItemsFromHeaderCheckbox.map((item: any) => item[this.uniqueId]);
    } else {
      this.selectedItem = [];
    }
  }

  public getHeaderRowStyle(): { [klass: string]: any } {
    // Header is treated as the row preceding the first body row, so it takes
    // the opposite colour of the first row's alternating colour.
    if (this.tableRowBgColour?.selectRowBackgroundStyle === 'alternate-colours') {
      return {
        ...this.tableHeaderCss,
        'background-color': this.tableRowBgColour?.evenRowBgColor,
      };
    }

    return this.tableHeaderCss;
  }

  public getRowStyle(rowData: any, rowIndex: number): { [klass: string]: any } {
    const baseStyle = rowData?.entityStyle ? rowData.entityStyle : {};
    const index = this.totalPageItemsCount * this.pageSize + rowIndex;
    // Apply alternate row colors if selectRowBackgroundStyle is alternate-colours
    if (this.tableRowBgColour?.selectRowBackgroundStyle === 'alternate-colours') {
      const bgColor =
        (index + 1) % 2 === 0 ? this.tableRowBgColour?.evenRowBgColor : this.tableRowBgColour?.oddRowBgColor;

      return {
        ...this.tableRowStyle,
        'background-color': bgColor,
        ...baseStyle,
        ...this.tableRowCss,
      };
    }

    // Default fallback (no alternate background color)
    return {
      ...(rowData?.entityStyle && Object.keys(rowData.entityStyle).length
        ? rowData.entityStyle
        : this.tableRowStyle),
      ...this.tableRowCss,
    };
  }

  public onClickRow(event: any, rowData: any) {
    const target = event.target as HTMLElement;
    if (target && target.id === 'td-data' && this.enableClickableRow) {
      this.rowData.emit(rowData);
    }
    const isMoreOptions = hasParentWithId(target, 'more-options');
    if (isMoreOptions) {
      if (this.isSelectedId === rowData[this.uniqueId] && this.isShowOption) {
        this.isShowOption = false;
        this.isSelectedId = '';
      } else {
        // Open the menu for this row
        this.isShowOption = true;
        this.isSelectedId = rowData[this.uniqueId];
      }
    }
  }

  public get currentPage(): number {
    return this.pageSize > 0 ? Math.floor(this.first / this.pageSize) + 1 : 1;
  }

  public get totalPages(): number {
    if (!this.pageSize || isNaN(this.totalListCount)) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalListCount / this.pageSize));
  }

  public getPageNumbers(): number[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const maxVisible = 5;

    let start = Math.max(1, current - Math.floor(maxVisible / 2));
    const end = Math.min(total, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);

    const pages: number[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  public goToPage(page: number) {
    if (page === this.currentPage) {
      return;
    }
    const rows = this.pageSize;
    this.onPageChange(this.buildPageEvent((page - 1) * rows, rows));
    this.scrollToTopOnPaginationChange();
  }

  public goToFirstPage() {
    this.goToPage(1);
  }

  public goToLastPage() {
    this.goToPage(this.totalPages);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // If click is NOT inside the popup or more-options button, close menu
    if (!target.closest('#more-options')) {
      this.isShowOption = false;
      this.isSelectedId = '';
    }
  }

  public dropDownValueChange(event: string) {
    this.dropDownValue.emit(event);
  }

  public onClickConfirmButton(event: any) {
    this.confirmButtonAction.emit(event);
  }

  public get skeletonEnabled(): boolean {
    return !!this.skeletonConfig?.enabled;
  }

  public get skeletonRowIndexes(): number[] {
    return Array.from({ length: this.skeletonRowCount }, (_, index) => index);
  }

  public skeletonCellAt(rowIndex: number, columnIndex: number): SkeletonCell {
    const columnCount = Math.max(1, this.columns.length);
    return (
      getSkeletonTableCell(this.skeletonCells(columnCount), rowIndex, columnIndex, columnCount) ??
      SKELETON_FALLBACK_CELL
    );
  }

  private get skeletonRowCount(): number {
    return deriveSkeletonTableRowCount({ pagingEnabled: this.pagingEnabled, pageSize: this.pageSize });
  }

  /** Cached, because the template asks for a cell once per row and column on every render. */
  private skeletonCells(columnCount: number): SkeletonCell[] {
    const key = `${this.skeletonRowCount}x${columnCount}:${JSON.stringify(this.skeletonConfig ?? {})}`;
    if (this.skeletonCellsCache?.key !== key) {
      this.skeletonCellsCache = {
        key,
        cells: buildSkeletonTableCells(this.skeletonConfig, this.skeletonRowCount, columnCount),
      };
    }
    return this.skeletonCellsCache.cells;
  }
}

export const desktopUserTemplate = (tableData: string) => `${tableData}`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const desktopUserColumnTemplate = (tableHeader: any[]) => `
  ${tableHeader}
`;
